'use client'

import React from 'react'
import Link from 'next/link'
import BlogPostLayout from '@/app/components/BlogPostLayout'
import { BlogTLDR, BlogKeyTakeaways, BlogFAQ, BlogSources, BlogLastUpdated, BlogComparisonTable, BlogAppCTA } from '@/app/components/blog/BlogAeo'
import KeyStats from '@/app/components/blog/KeyStats'

export default function PostContent() {
  return (
    <BlogPostLayout
      title="Blending Ice for 30 Seconds Released Hundreds of Millions of Plastic Particles"
      author="MicroPlastics Research Desk"
      date="August 20, 2026"
      readTime="12 min read"
      heroImage="/blog/heroes/home.webp"
      heroImageAlt="A plastic blender jar releases microplastics and nanoplastics when ice scratches the inner wall at high speed during blending"
    >
      <BlogLastUpdated iso="2026-08-20" formatted="August 20, 2026" />

      <BlogTLDR>
        <strong>Yes, and by a wide margin — a blender is the most abrasive
        thing most kitchens own.</strong> When researchers at the University of
        Newcastle blended ice blocks in water for thirty seconds in an ordinary
        plastic jar and analysed the result with Raman imaging, they estimated{' '}
        <strong>roughly 0.36 to 0.78 billion micro- and nanoplastic particles
        released from the container</strong> in that half-minute. The mechanism
        is not heat and not leaching: the blade throws a vortex that drives ice
        against the inner wall, and the wall loses material. That is why{' '}
        <strong>&ldquo;BPA-free&rdquo; and &ldquo;Tritan&rdquo; do not answer
        this question at all</strong> — they describe what the plastic is made
        of, not what happens when you sandblast it with frozen fruit. It also
        means the fixes are mechanical and mostly free: fewer frozen
        ingredients, shorter runs, and retiring a jar once it goes cloudy. If
        you are replacing a blender, a glass or stainless jar removes the
        variable entirely. One important caveat before you throw anything out:
        this was <strong>one blender under one worst-case condition</strong>,
        the paper reported no per-serving figure for ordinary smoothies, and
        anyone quoting you particles-per-year has invented it.
      </BlogTLDR>

      <BlogKeyTakeaways
        items={[
          <><strong>Thirty seconds of blending ice released an estimated 0.36–0.78 billion particles</strong> from the plastic jar — micro- and nanoplastics together, measured by Raman imaging.</>,
          <><strong>Abrasion is the mechanism, not heat or leaching.</strong> The high-speed vortex drives ice blocks against the container wall and scrapes debris off it.</>,
          <><strong>&ldquo;BPA-free&rdquo; is irrelevant here.</strong> BPA is a chemical that migrates out of certain polymers; this is the polymer itself being ground off. Tritan jars are BPA-free and still abrade.</>,
          <><strong>Hard, frozen and dry ingredients are the worst case.</strong> Ice, frozen fruit, nuts and dry grains are abrasives. Soft fruit in liquid is not the condition that was tested.</>,
          <><strong>A cloudy or scratched jar has already been abrading.</strong> Surface damage you can see is the mechanism made visible, and a scratched surface sheds more readily than a smooth one.</>,
          <><strong>Hot-soup programs stack both stressors.</strong> Vitamix states blade friction brings soup to 170 °F in about five minutes on High — that is peak abrasion and peak heat in the same plastic jar.</>,
          <><strong>Glass and stainless jars exist.</strong> Vitamix sells a 48 oz stainless steel container; Oster ships several models with Boroclass glass jars. Both remove the shedding surface rather than improving it.</>,
          <><strong>Keep the finding in proportion.</strong> One blender, one condition, no health outcome measured, and no basis for a per-year exposure number.</>,
        ]}
      />

      <KeyStats
        heading="The numbers, and where each one comes from"
        stats={[
          { value: '0.36–0.78 bn', label: 'particles released in 30 seconds', context: 'micro- and nanoplastics from a plastic blender jar, blending ice blocks in Milli-Q water', source: 'Luo et al., Journal of Hazardous Materials, 2023', sourceUrl: 'https://pubmed.ncbi.nlm.nih.gov/37080027/' },
          { value: '30 s', label: 'the entire test duration', context: 'run with the pause function — one second blending, one second resting — not a long smoothie cycle', source: 'Luo et al., Journal of Hazardous Materials, 2023', sourceUrl: 'https://pubmed.ncbi.nlm.nih.gov/37080027/' },
          { value: '14.5–71.9 m', label: 'particles a year from a plastic chopping board', context: 'for comparison: polyethylene boards, per person per year, from the closest well-measured kitchen source', source: 'Yadav et al., Environmental Science & Technology, 2023', sourceUrl: 'https://pubs.acs.org/doi/10.1021/acs.est.3c00924' },
          { value: '170 °F', label: 'reached by blade friction alone in ~5 minutes', context: 'Vitamix’s own figure for its hot soup program — abrasion and heat applied to the same jar at once', source: 'Vitamix, How to heat and make homemade soups', sourceUrl: 'https://www.vitamix.com/us/en_us/what-you-can-make/hot-soups' },
        ]}
      />

      <h2>The short answer</h2>
      <p>
        Almost every microplastic story about kitchen appliances is really a
        story about heat — hot water through a plastic pod, a sous vide bag at
        60 °C, a kettle with a plastic reservoir. The blender is different, and
        that is what makes it interesting. Nothing about a smoothie is hot. The
        jar is doing something no other container in your kitchen does: it is
        holding a hard abrasive that is being flung against it, thousands of
        times a minute, on purpose.
      </p>
      <p>
        A plastic surface under that treatment loses material. When researchers
        actually looked for it, they found a great deal of it — and importantly,
        they found it in the nanoplastic range as well as the microplastic
        range, which is the size class that is hardest to measure and most able
        to move around the body.
      </p>

      <h2>What the study actually did</h2>
      <p>
        The paper is <em>Detection of microplastics and nanoplastics released
        from a kitchen blender using Raman imaging</em>, published in the{' '}
        <em>Journal of Hazardous Materials</em> in 2023 by a team at the Global
        Centre for Environmental Remediation, University of Newcastle,
        Australia. The framing in the abstract is disarmingly plain: they
        tested a blender they use in their own kitchen to make juice.
      </p>
      <p>
        The method was ice blocks in Milli-Q water — laboratory-pure water, so
        that anything found came from the blender rather than the ingredients —
        blended for thirty seconds using the pause function, roughly a second
        of blending followed by a second of rest. They filtered what came out
        and analysed it with Raman imaging, building a scanning spectrum matrix
        of 900 spectra in a 30 × 30 grid, then reconstructing images from it to
        pick out particles too small for conventional Raman resolution.
      </p>
      <p>
        Their estimate: <strong>approximately 0.36–0.78 × 10<sup>9</sup>{' '}
        micro- and nanoplastic particles released from the plastic container
        within thirty seconds</strong>. That is 360 to 780 million.
      </p>

      <h3>Why ice is the worst case</h3>
      <p>
        The mechanism the authors describe is straightforward. The blade
        generates a high-speed vortex; the vortex throws the ice blocks
        outward; the ice scratches the inner wall of the container; the wall
        sheds debris into the liquid. Ice is hard, angular, and does not
        deform. It is, functionally, a tumbling abrasive.
      </p>
      <p>
        This is worth holding onto because it sets the boundaries of the
        finding. Blending a ripe banana into oat milk is not this experiment.
        Blending a fistful of ice, frozen mango and almonds at maximum speed
        for ninety seconds is <em>more</em> than this experiment. The study
        establishes that the mechanism is real and that it operates at
        surprising scale; it does not tell you what your particular Tuesday
        smoothie produces.
      </p>

      <h3>What the number does and does not mean</h3>
      <p>
        A figure in the hundreds of millions is arresting, and it deserves
        three pieces of context:
      </p>
      <ul>
        <li>
          <strong>Most of those particles are nanoplastics, which are
          tiny.</strong> A count is not a mass. Hundreds of millions of
          nanometre-scale fragments can add up to a vanishingly small weight of
          plastic. Counts and masses tell different stories and the study
          reports a count.
        </li>
        <li>
          <strong>It is one blender.</strong> The authors did not name the
          model or identify the jar polymer, and they did not compare brands.
          Nobody has run the equivalent test across a shelf of blenders, which
          is the study the field actually needs.
        </li>
        <li>
          <strong>No health outcome was measured.</strong> This is a
          measurement paper about analytical method as much as about blenders.
          It tells you particles are released. It does not tell you what they
          do.
        </li>
      </ul>

      <h2>How this compares with the rest of your kitchen</h2>
      <p>
        The closest well-characterised comparison is the plastic chopping
        board. In a 2023 study in <em>Environmental Science &amp; Technology</em>,
        researchers estimated per-person annual exposure of{' '}
        <strong>7.4–50.7 g of microplastics from a polyethylene board and
        around 49.5 g from a polypropylene one</strong>, corresponding to
        roughly 14.5–71.9 million polyethylene particles a year, or about 79.4
        million polypropylene particles. Chopping carrots released more than
        chopping nothing, which is the same abrasion story.
      </p>
      <p>
        Set those side by side and the point lands: a year of chopping on a
        plastic board is in the tens of millions of particles, while thirty
        seconds of blending ice was estimated in the hundreds of millions. The
        two figures come from different methods and different size ranges, so
        this is an illustration of scale rather than a clean ratio — but no
        reasonable reading of them puts the blender low on the list.
      </p>
      <p>
        The same study also found that the released particles did not
        significantly change the viability of mouse cells in lab testing, which
        is a genuine and often-omitted part of that result. Detection is not
        the same as harm, and we would rather say so than let the number carry
        an implication the paper does not.
      </p>

      <BlogComparisonTable
        caption="Abrasion-driven microplastic sources in a normal kitchen (checked August 2026)"
        columns={[
          { key: 'i', label: 'Source' },
          { key: 'w', label: 'What was measured' },
          { key: 'm', label: 'What drives it' },
        ]}
        rows={[
          { i: 'Plastic blender jar', w: '~0.36–0.78 billion micro- and nanoplastic particles in 30 s blending ice', m: 'Hard abrasive driven against the wall at speed' },
          { i: 'Plastic chopping board', w: '14.5–71.9 million polyethylene particles per person per year', m: 'Blade cutting into the surface' },
          { i: 'Reusable bottle screw cap', w: '131 particles per litre after one opening, 242 after eleven', m: 'Thread grinding on thread' },
          { i: 'Kitchen sponge', w: 'Fragments shed continuously as the foam breaks down', m: 'Scrubbing abrasion plus material fatigue' },
          { i: 'Non-stick pan, once scratched', w: 'Coating fragments released from damaged areas', m: 'Utensil and cleaning abrasion' },
        ]}
      />

      <h2>Why &ldquo;BPA-free&rdquo; and &ldquo;Tritan&rdquo; miss the point</h2>
      <p>
        Blender marketing is almost entirely about material chemistry. Vitamix
        states its containers are made from BPA-free Eastman Tritan
        copolyester. Blendtec says the same of its jars, adding NSF food-safety
        certification. NutriBullet uses Tritan too, and has moved its blending
        cups to Tritan Renew, made with 50% certified recycled content.
      </p>
      <p>
        Every one of those claims is true, and none of them addresses what the
        study measured.{' '}
        <strong>BPA is a chemical that migrates out of certain polymers.
        A microplastic is a fragment of the polymer itself.</strong> A jar can
        be entirely free of the first and still produce the second — and the
        second is what a vortex full of ice generates.
      </p>
      <p>
        We have seen this exact pattern before.{' '}
        <Link href="/blog/is-tritan-a-microplastic" className="text-forest underline">When researchers counted particles from reusable Tritan bottles</Link>,
        the source was not the wall of the bottle but the screw cap: 131
        particles per litre after a single opening, rising to 242 after eleven.
        The bottle body was fine. The place where two plastic surfaces ground
        against each other was not. Tritan is a genuinely good drinkware
        plastic — tough, clear, no bisphenol in its structure — and it is still
        a plastic being abraded.
      </p>

      <h2>What your blender jar is actually made of</h2>
      <p>
        Manufacturers publish this, so it is worth checking rather than
        assuming. All of the below is taken from the brands&rsquo; own
        sites:
      </p>

      <BlogComparisonTable
        caption="Blender jar materials, from manufacturers’ own published specifications (checked August 2026)"
        columns={[
          { key: 'b', label: 'Brand' },
          { key: 'j', label: 'Jar material as published' },
          { key: 'n', label: 'Non-plastic option?' },
        ]}
        rows={[
          { b: 'Vitamix', j: 'BPA-free Eastman Tritan copolyester, hardened stainless blade', n: 'Yes — a 48 oz stainless steel container is sold separately' },
          { b: 'Blendtec', j: 'Eastman Tritan copolyester, BPA-free, NSF certified', n: 'Not published' },
          { b: 'NutriBullet', j: 'Tritan BPA-free plastic; cups moving to Tritan Renew (50% certified recycled content). Food processor bowl is BPA-free SAN', n: 'Not published' },
          { b: 'Oster', j: 'Several Classic Series models ship with a 6-cup Boroclass glass jar', n: 'Yes — glass jar is the standard fitment on those models' },
        ]}
      />

      <p>
        Two things stand out. The first is that the premium end of the market
        is uniformly Tritan, so paying more does not move you off plastic — it
        moves you to a better-engineered plastic that is still the abraded
        surface. The second is that the two straightforward non-plastic routes
        are a Vitamix stainless container, which fits an appliance many people
        already own, and an inexpensive Oster with a glass jar. Neither is a
        premium purchase.
      </p>

      <BlogAppCTA
        headline="Not sure what the plastic in your kitchen actually is?"
        sub="Scan a jar, a lid or a storage box and get the polymer, a 0–100 risk score, and a lower-plastic swap in about five seconds."
      />

      <h2>Five things that change how much your blender sheds</h2>
      <p>
        Every one of these acts on abrasion, because abrasion is the mechanism.
        In rough order of effect:
      </p>
      <ul>
        <li>
          <strong>How frozen and how hard the ingredients are.</strong> Ice is
          the worst case and it is the one that was tested. Letting frozen
          fruit sit out for five or ten minutes, using chilled fruit instead of
          frozen, or adding the liquid first so the solids are cushioned all
          reduce the direct ice-on-wall contact the study describes.
        </li>
        <li>
          <strong>How long you run it, and how fast.</strong> The result came
          from thirty seconds. Ninety seconds on maximum is three times the
          exposure at higher vortex energy. Most smoothies are blended
          considerably longer than they need to be.
        </li>
        <li>
          <strong>How scratched the jar already is.</strong> A cloudy,
          scuffed, chalky-looking jar has been abrading for years. A damaged
          surface has more exposed area and shears more readily than a smooth
          one. If you can see the wear, the jar is past its useful life for
          this purpose — and it is the cheapest single part to replace.
        </li>
        <li>
          <strong>Whether you dry-grind in the same jar.</strong> Grains, nuts
          and coffee beans with no liquid to cushion them are the harshest
          thing you can do to a plastic container. Vitamix sells a separate dry
          grains container precisely because the job is different. If you grind,
          do not do it in the jar you make smoothies in.
        </li>
        <li>
          <strong>Whether you use the hot soup program.</strong> Vitamix states
          that blade friction alone brings soup to 170 °F in about five minutes
          on High. That is five minutes of maximum-speed abrasion applied to a
          jar wall that is simultaneously being heated toward the top of what
          the polymer is comfortable with. Nobody has measured particle release
          under that specific combination, which is exactly why it is worth
          flagging: it stacks both known stressors at once, and blending soup
          on the stove first and warm — not boiling — afterwards avoids the
          question.
        </li>
      </ul>

      <h2>What to buy if you are replacing one</h2>
      <ul>
        <li>
          <strong>A stainless container, if your machine takes one.</strong>{' '}
          Vitamix&rsquo;s 48 oz stainless steel container is the cleanest
          upgrade for an existing owner: no shedding surface, and you keep the
          motor you already paid for. The trade-off is that you cannot see what
          is happening inside.
        </li>
        <li>
          <strong>A glass jar.</strong> Oster&rsquo;s Classic Series models
          ship with a 6-cup Boroclass glass jar as standard and cost a fraction
          of a high-performance blender. Glass is heavy and can chip, but it
          does not abrade into your food.
        </li>
        <li>
          <strong>An immersion blender used in a metal pot.</strong> For soups
          especially, a stick blender with a stainless shaft and bell, used
          directly in a saucepan, removes the plastic vessel from the equation
          altogether. Check the bell — some are plastic — and this is a genuine
          replacement only for soups and purées, not for frozen smoothies.
        </li>
        <li>
          <strong>Nothing at all, if your blender works.</strong> Replacing a
          functioning appliance on the strength of one study on one unit is not
          a proportionate response. Changing how you use it is free and acts on
          the same variable.
        </li>
      </ul>

      <h3>What is not worth doing</h3>
      <p>
        Straining a smoothie will not help — a mesh that catches nanoplastics
        would not pass liquid. Buying a &ldquo;BPA-free&rdquo; replacement jar
        does not address the mechanism, as above. And{' '}
        <Link href="/blog/do-microplastic-removal-supplements-work" className="text-forest underline">microplastic &ldquo;detox&rdquo; supplements</Link>{' '}
        have no evidence behind them regardless of where the particles came
        from.
      </p>

      <h2>What we still do not know</h2>
      <ul>
        <li>
          <strong>No brand comparison exists.</strong> One unnamed blender has
          been tested. Whether jar polymer, wall thickness, blade geometry or
          motor speed dominates is unknown, and it is the obvious next
          experiment.
        </li>
        <li>
          <strong>Ordinary smoothies have not been measured.</strong> The
          tested condition was ice in pure water. Soft fruit, yoghurt, protein
          powder and oat milk behave differently and nobody has quantified how
          differently.
        </li>
        <li>
          <strong>Counts are not doses.</strong> The paper reports particle
          counts, not mass, and translating either into an exposure estimate
          would require assumptions the study does not provide.
        </li>
        <li>
          <strong>The heat-plus-abrasion case is unstudied</strong> despite
          being a marketed feature on high-performance machines.
        </li>
      </ul>

      <p>
        Related reading:{' '}
        <Link href="/blog/is-tritan-a-microplastic" className="text-forest underline">is Tritan a microplastic</Link>,{' '}
        <Link href="/blog/plastic-cutting-board-microplastics" className="text-forest underline">plastic cutting boards</Link>,{' '}
        <Link href="/blog/microplastics-in-electric-kettles" className="text-forest underline">electric kettles</Link>,{' '}
        <Link href="/blog/do-air-fryers-release-microplastics" className="text-forest underline">air fryers</Link>,{' '}
        <Link href="/blog/microplastics-in-protein-powder-supplements" className="text-forest underline">protein powder</Link>, and{' '}
        <Link href="/blog/is-it-plastic" className="text-forest underline">which kitchen materials are actually plastic</Link>.
      </p>

      <BlogFAQ
        items={[
          {
            question: 'Do plastic blenders release microplastics?',
            answer: 'Yes. In a 2023 study published in the Journal of Hazardous Materials, researchers blended ice blocks in laboratory-pure water for 30 seconds and estimated that roughly 0.36–0.78 billion micro- and nanoplastic particles were released from the plastic container. The mechanism is abrasion: the blade creates a high-speed vortex that drives ice against the inner wall, which sheds debris. The result comes from a single blender under a deliberately harsh condition.',
          },
          {
            question: 'Does a BPA-free or Tritan jar solve the problem?',
            answer: 'No, because it answers a different question. BPA is a chemical that migrates out of certain plastics; a microplastic is a fragment of the plastic itself. Vitamix, Blendtec and NutriBullet all publish that their jars are BPA-free Eastman Tritan copolyester, and that is accurate — but a Tritan jar being scoured by ice still loses surface material. The comparable finding on reusable bottles put the particles at the screw cap, where two plastic surfaces grind together, not in the bottle wall.',
          },
          {
            question: 'Is blending ice worse than blending soft fruit?',
            answer: 'Almost certainly, though only ice has been tested. The published mechanism is that hard, angular ice blocks are thrown against the container wall by the vortex and scratch it. Soft fruit deforms instead of scratching. Frozen fruit, nuts and dry grains sit at the abrasive end alongside ice; ripe fruit blended into liquid sits at the other. Letting frozen ingredients soften for five to ten minutes and adding liquid first both reduce direct contact.',
          },
          {
            question: 'Should I get a glass or stainless steel blender jar?',
            answer: 'If you are replacing a jar or a blender anyway, yes — it removes the shedding surface rather than improving it. Vitamix sells a 48 oz stainless steel container that fits machines many people already own, and several Oster Classic Series models ship with a 6-cup Boroclass glass jar as standard. Glass is heavier and can chip; stainless is opaque so you cannot see the blend. Replacing a working plastic blender purely over this is harder to justify than simply using it differently.',
          },
          {
            question: 'How does a blender compare to a plastic cutting board?',
            answer: 'A 2023 study in Environmental Science & Technology estimated 14.5–71.9 million polyethylene microplastic particles per person per year from a plastic chopping board, alongside roughly 7.4–50.7 g of material. The blender study estimated hundreds of millions of particles from 30 seconds of blending ice. The two used different methods and covered different size ranges, so it is not a clean ratio — but blending is clearly not a minor source. The board study also found the released particles did not significantly reduce mouse-cell viability in testing.',
          },
          {
            question: 'Is the hot soup setting on a high-performance blender a problem?',
            answer: 'It has not been measured, which is precisely why it is worth noting. Vitamix states that blade friction alone brings soup to 170 °F in about five minutes on High. That combines sustained maximum-speed abrasion with elevated temperature in the same plastic jar — the two conditions known independently to increase plastic shedding. Heating soup on the stove and blending it warm rather than near-boiling sidesteps the question entirely.',
          },
          {
            question: 'When should I replace my blender jar?',
            answer: 'When it goes cloudy, scuffed or chalky. Visible surface damage means the wall has been abrading, and a roughened surface sheds more readily than a smooth one. Jars are sold separately by most manufacturers and are far cheaper than a new machine. Avoid abrasive scouring pads, and if you grind grains, nuts or coffee, use a separate dry container rather than the jar you make smoothies in.',
          },
          {
            question: 'How many microplastics am I swallowing from my smoothie?',
            answer: 'Nobody knows, and any specific figure you see is invented. The published study measured one unnamed blender running ice in pure water for 30 seconds and reported a particle count, not a mass or a per-serving dose. It did not test ordinary smoothie ingredients, did not compare brands, and did not measure any health outcome. It establishes that the mechanism is real and operates at large particle counts — not what reaches you over a year.',
          },
        ]}
      />

      <BlogSources
        items={[
          { authors: 'Luo Y, Awoyemi OS, Naidu R, Fang C', year: 2023, title: 'Detection of microplastics and nanoplastics released from a kitchen blender using Raman imaging — approximately 0.36–0.78 × 10⁹ micro- and nanoplastic particles released from the plastic container within 30 seconds of blending', publisher: 'Journal of Hazardous Materials 453:131403', url: 'https://pubmed.ncbi.nlm.nih.gov/37080027/' },
          { authors: 'Yadav H, Khan MRH, Quadir M, et al.', year: 2023, title: 'Cutting boards: an overlooked source of microplastics in human food? — estimated 7.4–50.7 g/year from polyethylene boards and ~49.5 g/year from polypropylene, equivalent to roughly 14.5–71.9 million polyethylene particles per person per year', publisher: 'Environmental Science & Technology', url: 'https://pubs.acs.org/doi/10.1021/acs.est.3c00924' },
          { authors: 'Vitamix', year: 2026, title: 'Blender container specifications — BPA-free Eastman Tritan copolyester containers and a 48-ounce stainless steel container', publisher: 'Vitamix (manufacturer)', url: 'https://www.vitamix.com/us/en_us/products/48-ounce-stainless-steel-container' },
          { authors: 'Vitamix', year: 2026, title: 'How to heat and make homemade soups in a Vitamix — blade friction brings soup to 170 °F in about five minutes of blending on High', publisher: 'Vitamix (manufacturer)', url: 'https://www.vitamix.com/us/en_us/what-you-can-make/hot-soups' },
          { authors: 'Blendtec', year: 2026, title: 'Blendtec jars — Eastman Tritan copolyester, BPA-free, NSF certified', publisher: 'Blendtec (manufacturer)', url: 'https://www.blendtec.com/collections/jars' },
          { authors: 'NutriBullet', year: 2026, title: 'Tritan Renew sustainable blending cups — Tritan BPA-free plastic, cups made with 50% certified recycled content', publisher: 'NutriBullet (manufacturer)', url: 'https://www.nutribullet.com/sustainability/' },
          { authors: 'Oster', year: 2026, title: 'Classic Series Heritage Blender with 6-Cup Boroclass glass jar', publisher: 'Oster (manufacturer)', url: 'https://www.oster.com/blenders/kitchen-blenders/oster-classic-series-heritage-blender-with-6-cup-glass-jar-stainless-steel/SP_395331.html' },
        ]}
      />
    </BlogPostLayout>
  )
}
